/** Squarified treemap layout (Bruls et al.) — no chart library needed.
 *
 * Lays items into a plane so each tile's area is proportional to its
 * value, keeping tiles as square as possible. Used by the market heatmap:
 * tile area = index weight, tile color = day change.
 */

export interface TreemapDatum {
  id: string;
  value: number;
}

export interface TreemapTile<T extends TreemapDatum> {
  datum: T;
  /** Percentages of the container (0-100), ready for absolute positioning. */
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Entry<T extends TreemapDatum> {
  datum: T;
  area: number;
}

/** Worst aspect ratio in a row of areas laid against a side of length `side`. */
function worstAspect(row: readonly number[], side: number): number {
  const sum = row.reduce((a, b) => a + b, 0);
  const max = Math.max(...row);
  const min = Math.min(...row);
  return Math.max((side * side * max) / (sum * sum), (sum * sum) / (side * side * min));
}

/** Grow the row from `start` while its worst tile keeps getting more square. */
function rowEnd(areas: readonly number[], start: number, side: number): number {
  let end = start + 1;
  let best = worstAspect(areas.slice(start, end), side);
  while (end < areas.length) {
    const next = worstAspect(areas.slice(start, end + 1), side);
    if (next > best) break;
    best = next;
    end += 1;
  }
  return end;
}

/** Lay one row along the shorter edge of `free`; return tiles + the leftover. */
function placeRow<T extends TreemapDatum>(
  row: readonly Entry<T>[],
  free: Rect,
): { tiles: (Rect & { datum: T })[]; rest: Rect } {
  const side = Math.min(free.w, free.h);
  const thickness = row.reduce((a, e) => a + e.area, 0) / side;
  const horizontal = free.w >= free.h;
  const tiles: (Rect & { datum: T })[] = [];
  let offset = horizontal ? free.y : free.x;
  for (const e of row) {
    const length = e.area / thickness;
    tiles.push(
      horizontal
        ? { datum: e.datum, x: free.x, y: offset, w: thickness, h: length }
        : { datum: e.datum, x: offset, y: free.y, w: length, h: thickness },
    );
    offset += length;
  }
  const rest: Rect = horizontal
    ? { x: free.x + thickness, y: free.y, w: free.w - thickness, h: free.h }
    : { x: free.x, y: free.y + thickness, w: free.w, h: free.h - thickness };
  return { tiles, rest };
}

/**
 * @param aspect width/height of the rendering container — the layout runs in
 *   a plane of that shape so "square" tiles render square on screen.
 */
export function squarify<T extends TreemapDatum>(
  data: readonly T[],
  aspect = 1.6,
): TreemapTile<T>[] {
  const items = data.filter((d) => d.value > 0);
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => b.value - a.value);

  const planeW = 100 * aspect;
  const planeH = 100;
  const total = sorted.reduce((a, d) => a + d.value, 0);
  const scale = (planeW * planeH) / total;
  const entries: Entry<T>[] = sorted.map((d) => ({ datum: d, area: d.value * scale }));
  const areas = entries.map((e) => e.area);

  const placed: (Rect & { datum: T })[] = [];
  let free: Rect = { x: 0, y: 0, w: planeW, h: planeH };
  let i = 0;
  while (i < entries.length) {
    const end = rowEnd(areas, i, Math.min(free.w, free.h));
    const { tiles, rest } = placeRow(entries.slice(i, end), free);
    placed.push(...tiles);
    free = rest;
    i = end;
  }
  return placed.map((t) => ({
    datum: t.datum,
    x: (t.x / planeW) * 100,
    y: (t.y / planeH) * 100,
    w: (t.w / planeW) * 100,
    h: (t.h / planeH) * 100,
  }));
}
